/**
 * One saved chat per homeowner and one saved chat per agent.
 * Newest messages sit at the bottom. Older ones load when you scroll up.
 * "Start fresh" keeps the old messages on screen and stops using them
 * for the next answer.
 */

const { persistLabel } = require('./applianceLabel');

const THREAD_PAGE_SIZE = 30;
const SEARCH_MIN = 2;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const IMAGE_PATH_RE = /^assistant\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.(jpg|jpeg|png|webp)$/i;

const LABEL_LINES = [
  ['brand', 'Brand'],
  ['model', 'Model'],
  ['serial', 'Serial number'],
  ['dateCode', 'Date code'],
  ['printedDate', 'Date printed'],
  ['manufactureLabel', 'Made'],
  ['capacity', 'Size'],
  ['fuelOrVoltage', 'Power'],
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function ownerScope(access) {
  const subject = String((access && access.subject) || '');
  if (!UUID_RE.test(subject)) return null;
  if (access.kind === 'client') return { kind: 'homeowner', clientId: subject };
  if (access.kind === 'agent') return { kind: 'agent', agentId: subject };
  return null;
}

function isAssistantImagePath(ownerId, objectPath) {
  const id = String(ownerId || '').toLowerCase();
  const match = IMAGE_PATH_RE.exec(String(objectPath || ''));
  return Boolean(match && match[1].toLowerCase() === id);
}

function isQuotaPlaceholder(row) {
  if (!row) return false;
  return String(row.user_message || '') === 'agent question' && !String(row.ai_response || '').trim();
}

function messageTime(value) {
  const date = value instanceof Date ? value : new Date(value || 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dayKey(value) {
  const date = messageTime(value);
  if (!date) return '';
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function dayLabel(value, now) {
  const date = messageTime(value);
  const today = messageTime(now || new Date());
  if (!date || !today) return '';
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const diff = Math.round((start.getTime() - day.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  const month = MONTHS[date.getMonth()] || '';
  if (date.getFullYear() !== today.getFullYear()) {
    return `${month} ${date.getDate()}, ${date.getFullYear()}`;
  }
  return `${month} ${date.getDate()}`;
}

function labelLines(label) {
  const saved = persistLabel(label);
  if (!saved) return [];
  const lines = [];
  for (let index = 0; index < LABEL_LINES.length; index += 1) {
    const key = LABEL_LINES[index][0];
    const title = LABEL_LINES[index][1];
    if (saved[key]) lines.push({ key, title, value: saved[key] });
  }
  return lines;
}

function taskExtras(row) {
  const extra = row && row.vendor_recommendations;
  if (!extra || typeof extra !== 'object' || Array.isArray(extra)) {
    return { recommendedVendors: [], suggestTask: false, taskSuggestion: null };
  }
  return {
    recommendedVendors: Array.isArray(extra.recommendedVendors) ? extra.recommendedVendors : [],
    suggestTask: Boolean(extra.suggestTask && extra.taskSuggestion),
    taskSuggestion: extra.taskSuggestion || null,
  };
}

function rowsToMessages(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const messages = [];
  for (let index = 0; index < list.length; index += 1) {
    const row = list[index];
    if (!row || !row.id || isQuotaPlaceholder(row)) continue;
    const at = messageTime(row.created_at) || new Date();
    const epoch = Number(row.context_epoch) || 0;
    if (row.conversation_type === 'fresh_start') {
      messages.push({
        id: `${row.id}:fresh`,
        rowId: row.id,
        fresh: true,
        role: 'fresh',
        timestamp: at,
        contextEpoch: epoch,
      });
      continue;
    }
    const userText = String(row.user_message || '').trim();
    const answer = String(row.ai_response || '').trim();
    const imageUrl = row.image_url || '';
    const imageName = row.image_name || '';
    if (userText || imageUrl || imageName) {
      messages.push({
        id: `${row.id}:user`,
        rowId: row.id,
        role: 'user',
        content: userText,
        image: imageName,
        imageUrl,
        timestamp: at,
        contextEpoch: epoch,
      });
    }
    if (answer || row.appliance_info) {
      const extra = taskExtras(row);
      messages.push({
        id: `${row.id}:assistant`,
        rowId: row.id,
        role: 'assistant',
        content: answer,
        label: row.appliance_info || null,
        timestamp: at,
        recommendedVendors: extra.recommendedVendors,
        suggestTask: extra.suggestTask,
        taskSuggestion: extra.taskSuggestion,
        contextEpoch: epoch,
      });
    }
  }
  return messages;
}

function threadItems(messages, now) {
  const list = Array.isArray(messages) ? messages : [];
  const items = [];
  let lastDay = '';
  for (let index = 0; index < list.length; index += 1) {
    const msg = list[index];
    if (!msg) continue;
    if (msg.fresh) {
      items.push({ type: 'fresh', id: msg.id || `fresh-${index}` });
      continue;
    }
    const key = dayKey(msg.timestamp);
    if (key && key !== lastDay) {
      items.push({ type: 'day', id: `day-${key}`, label: dayLabel(msg.timestamp, now) });
      lastDay = key;
    }
    items.push({ type: 'message', message: msg });
  }
  return items;
}

function messagesInContext(messages) {
  const list = Array.isArray(messages) ? messages : [];
  let start = 0;
  for (let index = 0; index < list.length; index += 1) {
    if (list[index] && list[index].fresh) start = index + 1;
  }
  const kept = [];
  for (let index = start; index < list.length; index += 1) {
    const msg = list[index];
    if (msg && !msg.fresh) kept.push(msg);
  }
  return kept;
}

function answerHaystack(message) {
  if (!message || message.role !== 'assistant' || message.error) return '';
  const bits = [String(message.content || '')];
  const lines = labelLines(message.label);
  for (let index = 0; index < lines.length; index += 1) bits.push(lines[index].value);
  return bits.join(' ');
}

function searchAnswers(messages, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (needle.length < SEARCH_MIN) return [];
  const hits = [];
  const list = Array.isArray(messages) ? messages : [];
  for (let index = 0; index < list.length; index += 1) {
    const msg = list[index];
    if (answerHaystack(msg).toLowerCase().indexOf(needle) === -1) continue;
    hits.push({
      id: msg.rowId || msg.id,
      messageId: msg.id,
      createdAt: msg.timestamp,
    });
  }
  return hits;
}

function highlightParts(text, query) {
  const source = String(text || '');
  const needle = String(query || '').trim();
  if (needle.length < SEARCH_MIN) return [{ text: source, match: false }];
  const lower = source.toLowerCase();
  const find = needle.toLowerCase();
  const parts = [];
  let cursor = 0;
  while (cursor < source.length) {
    const at = lower.indexOf(find, cursor);
    if (at < 0) {
      parts.push({ text: source.slice(cursor), match: false });
      break;
    }
    if (at > cursor) parts.push({ text: source.slice(cursor, at), match: false });
    parts.push({ text: source.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }
  return parts.length ? parts : [{ text: source, match: false }];
}

function shouldLoadOlder(scrollTop, hasOlder, loading) {
  return Boolean(hasOlder) && !loading && Number(scrollTop) < 48;
}

/**
 * What to do with the message list after a render.
 * preserve: older messages were added above; keep the same lines on screen.
 * stay: the reader scrolled up, so a new message must not pull them down.
 * instant: first paint, jump to the newest line.
 * smooth: follow a new message while the reader is already at the bottom.
 */
function messageScrollAction({ stuck, loadingOlder, initial }) {
  if (loadingOlder) return 'preserve';
  if (!stuck) return 'stay';
  if (initial) return 'instant';
  return 'smooth';
}

function preservedScrollTop(previousTop, previousHeight, nextHeight) {
  const next = Number(previousTop) + (Number(nextHeight) - Number(previousHeight));
  return next < 0 ? 0 : next;
}

/**
 * The keyboard and the photo card shrink the message list. Scroll anchoring
 * is off (it jumps to the top in WebKit), so a reader already at the bottom
 * would lose the newest line under the composer. Keep that line in view.
 * A reader who scrolled up stays where they were.
 */
function scrollTopAfterResize({ stuck, scrollTop, scrollHeight, clientHeight }) {
  const top = Number(scrollTop) || 0;
  if (!stuck) return top;
  const next = (Number(scrollHeight) || 0) - (Number(clientHeight) || 0);
  return next < 0 ? 0 : next;
}

/** Move only the message list. scrollIntoView would also scroll the page. */
function scrollTopForMessage({ scrollTop, logTop, logHeight, nodeTop, nodeHeight }) {
  const next = Number(scrollTop) + (Number(nodeTop) - Number(logTop)) - (Number(logHeight) - Number(nodeHeight)) / 2;
  return next < 0 ? 0 : next;
}

function mergeOlder(existing, older) {
  const have = {};
  const current = Array.isArray(existing) ? existing : [];
  for (let index = 0; index < current.length; index += 1) {
    if (current[index] && current[index].id) have[current[index].id] = true;
  }
  const prefix = [];
  const incoming = Array.isArray(older) ? older : [];
  for (let index = 0; index < incoming.length; index += 1) {
    const msg = incoming[index];
    if (!msg || !msg.id || have[msg.id]) continue;
    have[msg.id] = true;
    prefix.push(msg);
  }
  return prefix.concat(current);
}

function ilikePattern(query) {
  const cleaned = String(query || '').replace(/[%_\\]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
  if (cleaned.length < SEARCH_MIN) return '';
  return `%${cleaned}%`;
}

function cursorFilter(cursor) {
  if (!cursor) return '';
  const at = String(cursor.createdAt || '');
  const id = String(cursor.id || '');
  if (!/^\d{4}-\d{2}-\d{2}T[0-9:.+-]+Z?$/.test(at)) return '';
  if (!UUID_RE.test(id)) return '';
  return `created_at.lt."${at}",and(created_at.eq."${at}",id.lt.${id})`;
}

function sampleThread(now) {
  const today = messageTime(now) || new Date();
  const oct21 = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 3, 9, 41);
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1, 16, 5);
  const morning = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 9, 12);
  return [
    {
      id: 'sample-photo:user',
      rowId: 'sample-photo',
      role: 'user',
      content: 'What does this water heater label say?',
      image: 'label.jpg',
      timestamp: oct21,
    },
    {
      id: 'sample-photo:assistant',
      rowId: 'sample-photo',
      role: 'assistant',
      content: 'This is a State water heater. It was made in November 2008. As of today, it is about 18 years old. Plan to replace it if it starts leaking.',
      label: {
        brand: 'State',
        model: 'GS6-40',
        serial: '0847A024271',
        manufactureLabel: 'November 2008',
      },
      suggestTask: true,
      taskSuggestion: { title: 'Plan to replace the water heater', category: 'plumbing' },
      timestamp: new Date(oct21.getTime() + 60000),
    },
    {
      id: 'sample-filter:user',
      rowId: 'sample-filter',
      role: 'user',
      content: 'How often should I change my HVAC filter?',
      timestamp: yesterday,
    },
    {
      id: 'sample-filter:assistant',
      rowId: 'sample-filter',
      role: 'assistant',
      content: 'Change a standard 1-inch filter about every 90 days. Homes with pets usually need a new filter every 30 to 60 days.',
      timestamp: new Date(yesterday.getTime() + 60000),
    },
    {
      id: 'sample-today:user',
      rowId: 'sample-today',
      role: 'user',
      content: 'Does that water heater date change how often I flush it?',
      timestamp: morning,
    },
    {
      id: 'sample-today:assistant',
      rowId: 'sample-today',
      role: 'assistant',
      content: 'Yes. A State heater from November 2008 should be flushed once a year. Watch for rust-colored water.',
      label: {
        brand: 'State',
        serial: '0847A024271',
        manufactureLabel: 'November 2008',
      },
      timestamp: new Date(morning.getTime() + 60000),
    },
  ];
}

module.exports = {
  THREAD_PAGE_SIZE,
  SEARCH_MIN,
  ownerScope,
  isAssistantImagePath,
  isQuotaPlaceholder,
  dayKey,
  dayLabel,
  labelLines,
  rowsToMessages,
  threadItems,
  messagesInContext,
  searchAnswers,
  highlightParts,
  shouldLoadOlder,
  messageScrollAction,
  preservedScrollTop,
  scrollTopAfterResize,
  scrollTopForMessage,
  mergeOlder,
  ilikePattern,
  cursorFilter,
  sampleThread,
};
